import { 
    Box, 
    Text, 
    Progress, 
    Button, 
    Flex,
    IconButton, 
    useDisclosure, 
    Drawer, 
    DrawerOverlay, 
    DrawerContent, 
    DrawerHeader, 
    DrawerBody, 
    DrawerCloseButton,
    Icon
} from "@chakra-ui/react";
import { FaInfoCircle, FaTicketAlt, FaLightbulb, FaClock } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import dateUtils from "../../utils/formatDate";

const RewardProgress = ({ textColor, currentStamps, maxStamps = 5, onRedeem }) => {
  const stampsLeft = 5 - (isNaN(currentStamps) ? 0 : currentStamps);
  const isRedeemable = currentStamps >= maxStamps;
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { t } = useTranslation();
  const savedLanguage = localStorage.getItem("i18nextLng") || "en";

  return (
    <Box w="100%" maxW="100%" textAlign="start" color="gray.800">
      {/* Stamp Progress Info Row */}
      <Flex align="center" p={1} mb={1}>
        {/* Left side: IconButton and Text */}
        <Flex gap={0}> {/* Set gap to 0 to stick them together */}
          <IconButton
            mt={1}
            ml={-3}
            icon={<FaInfoCircle />}
            size="xs"
            color="blue.300"
            aria-label="Stamp Info"
            onClick={onOpen}
            variant="ghost"
          />
          <Text fontSize={["xs", "sm"]} color={textColor} pr={2} mt={1}> {/* Add ml for small spacing */}
            {/* Your stamp campaign */}
            {t('your_stamp_campaign')}
          </Text>
        </Flex>

        {/* Right side: Stamp count */}
        <Text fontSize={["xs", "sm"]} fontWeight="semibold" color={textColor}>
          {dateUtils.convertToBurmeseNumerals(`(${currentStamps ?? 0}/${maxStamps})`, savedLanguage)}
        </Text>

      </Flex>

      {/* Info Drawer */}
      <Drawer isOpen={isOpen} placement="bottom" onClose={onClose}>
        <DrawerOverlay />
        <DrawerContent borderTopRadius="2xl" p={4}>
          <DrawerCloseButton />
          <DrawerHeader fontSize="md" fontWeight="bold">
            {t('stamp_guide.header')}
          </DrawerHeader>
          <DrawerBody color={textColor}>
            {/* Stamp Collection Information */}
            <Box mb={6}>
              <Flex align="center" mb={2}>
                <Icon as={FaTicketAlt} color="teal.500" mr={2} />
                <Text fontSize="sm" fontWeight="bold">
                  {t('stamp_guide.collection.title')}
                </Text>
              </Flex>
              <Text fontSize="sm" mb={2} dangerouslySetInnerHTML={{
                __html: t('stamp_guide.collection.single_purchase')
              }} />
              <Text fontSize="sm" dangerouslySetInnerHTML={{
                __html: t('stamp_guide.collection.reward_condition', { 
                  count: 5, 
                })
              }} />
            </Box>

            {/* Stamp Expiry Information */}
            <Box mb={6}>
              <Flex align="center" mb={2}>
                <Icon as={FaClock} color="orange.500" mr={2} />
                <Text fontSize="sm" fontWeight="bold">
                  {t('stamp_guide.expiry.title')}
                </Text>
              </Flex>
              <Text fontSize="sm" dangerouslySetInnerHTML={{
                __html: t('stamp_guide.expiry.description', { months: 6 })
              }} />
            </Box>

            {/* Additional Tips */}
            <Box>
              <Flex align="center" mb={2}>
                <Icon as={FaLightbulb} color="yellow.500" mr={2} />
                <Text fontSize="sm" fontWeight="bold">
                  {t('stamp_guide.tips.title')}
                </Text>
              </Flex>
              <Box as="ul" fontSize="sm" pl={4}>
                <Box as="li" mb={2}>{t('stamp_guide.tips.check_progress')}</Box>
                <Box as="li" mb={2}>{t('stamp_guide.tips.redeem_early')}</Box>
              </Box>
            </Box>
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      {/* Progress Bar */}
      <Progress
        value={(currentStamps / maxStamps) * 100}
        min={0}
        max={100}
        size="sm"
        colorScheme="green"
        borderRadius="full"
        height="6px"
        mb={2}
      />

      {/* Stamps Needed for Next Reward */}
      {!isRedeemable ? (
        <Text fontSize={["2xs", "xs"]} color={textColor}>
          {/* Collect <b>{stampsLeft}</b> more stamp{stampsLeft > 1 ? "s" : ""} to get your 10% off! */}
          {t('reward_progress.collect_discount')}<b> {stampsLeft}</b>{t('reward_progress.collect_stamp')}
          
        </Text>
      ) : (
        <Flex justify="space-between" align="center" mt={1}>
          {/* Text on the left */}
          <Text fontSize={["2xs", "xs"]} color={textColor}>
            {/* You’ll get a 10% off on your 6th purchase! */}
            {t('reward_progress.sixth_purchase_discount')}
          </Text>

          {/* Button on the right */}
          <Button
            bgGradient="linear(to-r, yellow.400, orange.400)" // Gradient from yellow to orange
            color="black"
            size="xs"
            fontWeight="light"
            borderRadius="lg"
            _hover={{ bgGradient: "linear(to-r, yellow.500, orange.500)" }} // Gradient on hover
            onClick={onRedeem}
            transition="0.3s"
            px={[2, 3]} // Adjust padding for smaller screens
            py={[1, 2]}
            fontSize={["2xs", "xs"]} // Responsive button text
          >
            {t('reward_progress.button')}
          </Button>
        </Flex>
      )}
    </Box>
  );
};

export default RewardProgress;